import { useCallback, useState } from 'preact/hooks';
import { formatMorphologyTypeLabel } from '../../../lib/portal-detail-card-contract.js';
import { getSlopeColorHex, SLOPE_3D_LEGEND } from '../../parcelSlope/ParcelSlopeThreeCard/slopeThreeColors.js';
import ParcelMorphologyThreeScene from './ParcelMorphologyThreeScene.jsx';
import './parcel-morphology-three-card.css';

function fmtPct(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const pct = n <= 1 ? n * 100 : n;
  return `%${pct.toLocaleString('tr-TR', { maximumFractionDigits: 0 })}`;
}

function fmtM(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  return `${Number(v).toLocaleString('tr-TR', { maximumFractionDigits: 1 })} m`;
}

function fmtDelta(v) {
  if (v == null || Number.isNaN(Number(v))) return '—';
  const n = Number(v);
  const sign = n > 0 ? '+' : '';
  return `${sign}${n.toLocaleString('tr-TR', { maximumFractionDigits: 1 })} m`;
}

export default function ParcelMorphologyThreeCard({
  data = null,
  loading = false,
  error = null,
  compact = false,
  onReady = null,
}) {
  const [webglOk, setWebglOk] = useState(true);
  const onUnsupported = useCallback(() => setWebglOk(false), []);

  if (loading) {
    return (
      <div className={`pp-pcm-three pp-pcm-three--loading${compact ? ' pp-pcm-three--compact' : ''}`} aria-busy="true">
        <div className="pp-pcm-three__skeleton" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={`pp-pcm-three pp-pcm-three--error${compact ? ' pp-pcm-three--compact' : ''}`} role="alert">
        {error || '3D morfoloji buffer mesh hazırlanamadı'}
      </div>
    );
  }

  if (compact) {
    return (
      <div className="pp-pcm-three pp-pcm-three--compact" aria-label="200 m tampon morfoloji 3D">
        {!webglOk ? (
          <div className="pp-pcm-three__unsupported">3d desteklenmiyor</div>
        ) : (
          <div className="pp-pcm-three__stage pp-pcm-three__stage--compact">
            <ParcelMorphologyThreeScene
              data={data}
              compact
              onUnsupported={onUnsupported}
              onReady={onReady}
            />
          </div>
        )}
      </div>
    );
  }

  const stats = data.stats || {};
  const morph = data.morphology || {};
  const elev = data.elevations || {};
  const parcel = data.parcel || {};
  const adaParsel = [parcel.ada, parcel.parsel].filter(Boolean).join(' / ');
  const typeLabel = formatMorphologyTypeLabel(morph.type, morph.type_label || morph.type || stats.morphology_label);
  const bufferM = data.buffer_m ?? stats.buffer_m ?? morph.buffer_m ?? 200;
  const sides = Array.isArray(elev.sides) ? elev.sides : [];

  const rows = [
    { label: 'morfolojik tip', value: typeLabel || '—' },
    { label: 'guven', value: fmtPct(morph.confidence ?? stats.morphology_confidence) },
    { label: 'tampon', value: fmtM(bufferM) },
    { label: 'parsel ort. kot', value: fmtM(elev.parcel_mean_m) },
    { label: 'yukseklik araligi', value: fmtM(stats.elevation_range) },
    { label: 'ortalama egim', value: stats.avg_slope != null ? `%${Number(stats.avg_slope).toLocaleString('tr-TR', { maximumFractionDigits: 1 })}` : '—' },
    { label: 'kural', value: morph.reason || '—' },
  ];

  return (
    <article className="pp-pcm-three" aria-label="Parsel bağlam morfolojisi 3D buffer kartı">
      <header className="pp-pcm-three__head">
        <div>
          <h2 className="pp-pcm-three__title">parsel baglam morfolojisi</h2>
          <p className="pp-pcm-three__subtitle">
            {Number(bufferM)}
            {' m tampon yuzeyi · dem mesh (dilim cizimi yok)'}
          </p>
        </div>
        <div className="pp-pcm-three__meta">
          {typeLabel || '—'}
          {adaParsel ? ` · ${adaParsel}` : ''}
        </div>
      </header>

      <div className="pp-pcm-three__body">
        <div className="pp-pcm-three__main">
          <div className="pp-pcm-three__stage">
            {!webglOk ? (
              <div className="pp-pcm-three__unsupported">3d gorunum desteklenmiyor (webgl yok)</div>
            ) : (
              <ParcelMorphologyThreeScene
                data={data}
                onUnsupported={onUnsupported}
                onReady={onReady}
              />
            )}
          </div>
        </div>

        <aside className="pp-pcm-three__side">
          <div className="pp-pcm-three__summary">
            <h3 className="pp-pcm-three__panel-title">morfoloji ozeti</h3>
            <dl className="pp-pcm-three__summary-list">
              {rows.map((row) => (
                <div key={row.label} className="pp-pcm-three__summary-row">
                  <dt>{row.label}</dt>
                  <dd>{row.value}</dd>
                </div>
              ))}
            </dl>
          </div>

          {sides.length ? (
            <div className="pp-pcm-three__sides">
              <h3 className="pp-pcm-three__panel-title">dilim kotlari (metin)</h3>
              <ul className="pp-pcm-three__sides-list">
                {sides.map((side) => (
                  <li key={String(side.id)}>
                    <span>{String(side.id || '').toLowerCase()}</span>
                    <span>{fmtM(side.mean_m)}</span>
                    <span>{fmtDelta(side.delta_m)}</span>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          <div className="pp-pcm-three__legend">
            <h3 className="pp-pcm-three__panel-title">yuzey egim lejandi (%)</h3>
            <ul className="pp-pcm-three__legend-list">
              {SLOPE_3D_LEGEND.map((item) => (
                <li key={item.label}>
                  <span
                    className="pp-pcm-three__legend-swatch"
                    style={{ background: getSlopeColorHex((item.min + (item.max ?? 25)) / 2) }}
                  />
                  <span>
                    {item.min}
                    {' - '}
                    {item.max ?? '+'}
                    {' '}
                    {item.label}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </aside>
      </div>
    </article>
  );
}
